/**
 * Hook do PLD MENSAL autoritativo (Task #225/#29 — consome /api/v2/pld/mensal,
 * canonical_pld_mensal, 2001+). Cache keyed por submercado. TTL 5 min.
 *
 * `usePldMensalAll` chama 4× (SE/S/NE/N) pra sub-tab Histórico (gráfico + tabela
 * com 4 séries); combinação client-side porque o endpoint é por submercado.
 * Diferente do /historico (média diária, 2025+), o /mensal é o realizado
 * PUBLICADO (Zeus /rest/pld) e cobre 2001+.
 */
import { useEffect, useState } from "react";
import { getPldMensal } from "../api/client";
import type { PldMensalResponse, PldSubmercado } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 5 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<PldMensalResponse>;
}

const cache = new Map<string, CacheEntry>();

export function invalidatePldMensalCache(): void {
  cache.clear();
}

export interface UsePldMensalState {
  data: PldMensalResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function usePldMensal(submercado: string): UsePldMensalState {
  const [state, setState] = useState<UsePldMensalState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    const key = submercado;

    function load() {
      const now = Date.now();
      let entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        entry = { expiresAt: now + REFETCH_MS, promise: getPldMensal(submercado) };
        cache.set(key, entry);
      }
      entry.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [submercado]);

  return state;
}

export interface UsePldMensalAllState {
  bySubmercado: Record<PldSubmercado, PldMensalResponse | null>;
  isLoading: boolean;
  error: Error | null;
}

/** PLD mensal das 4 submercados (2001+) — gráfico + tabela do Histórico mensal. */
export function usePldMensalAll(): UsePldMensalAllState {
  const se = usePldMensal("SE");
  const s = usePldMensal("S");
  const ne = usePldMensal("NE");
  const n = usePldMensal("N");
  return {
    bySubmercado: { SE: se.data, S: s.data, NE: ne.data, N: n.data },
    isLoading: se.isLoading || s.isLoading || ne.isLoading || n.isLoading,
    error: se.error ?? s.error ?? ne.error ?? n.error,
  };
}
